// SPDX-License-Identifier: AGPL-3.0-or-later

import { create } from 'zustand';
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@intakeform/fhir';
import type { DraftAnswers } from '@intakeform/ui';
import {
  applyCalculations,
  projectAnswers,
  resolveActiveItems,
  walkResponseItems,
} from '@intakeform/validation';

export interface AttachmentRef {
  attachmentId: string;
  url: string;
  contentType: string;
  filename?: string;
}

export interface IntakeState {
  /** Per-linkId answer values (raw, before FHIR shaping). */
  answers: DraftAnswers;
  /** Per-linkId attachment references when the field is an attachment. */
  attachments: Record<string, AttachmentRef>;
  /** linkIds the patient has explicitly confirmed-still-correct (acks stale prefill). */
  confirmedStale: Set<string>;
  /** True when answers have changed and the autosave timer should run. */
  dirty: boolean;
  /** Last successful autosave; undefined if never saved. */
  lastSavedAt: Date | undefined;
  setAnswer: (linkId: string, value: unknown) => void;
  setAttachment: (linkId: string, ref: AttachmentRef) => void;
  confirmStale: (linkId: string) => void;
  hydrate: (answers: DraftAnswers, attachments: Record<string, AttachmentRef>) => void;
  markSaved: (at: Date) => void;
  reset: () => void;
}

export const useIntakeStore = create<IntakeState>((set) => ({
  answers: {},
  attachments: {},
  confirmedStale: new Set(),
  dirty: false,
  lastSavedAt: undefined,
  setAnswer: (linkId, value) =>
    set((s) => ({
      answers: { ...s.answers, [linkId]: value },
      dirty: true,
    })),
  setAttachment: (linkId, ref) =>
    set((s) => ({
      attachments: { ...s.attachments, [linkId]: ref },
      answers: { ...s.answers, [linkId]: ref.url },
      dirty: true,
    })),
  confirmStale: (linkId) =>
    set((s) => {
      const next = new Set(s.confirmedStale);
      next.add(linkId);
      return { confirmedStale: next, dirty: true };
    }),
  hydrate: (answers, attachments) => set({ answers, attachments, dirty: false }),
  markSaved: (at) => set({ lastSavedAt: at, dirty: false }),
  reset: () =>
    set({
      answers: {},
      attachments: {},
      confirmedStale: new Set(),
      dirty: false,
      lastSavedAt: undefined,
    }),
}));

/**
 * Inverse of {@link buildQuestionnaireResponse}: project a persisted
 * QuestionnaireResponse back to the store's `DraftAnswers` shape
 * for hydration. Used by the cross-session draft restore path
 * (intakeform-preview-kva) when GET /api/intake/:token returns a
 * `draft` field for this magic link.
 *
 * Multi-answer items (repeats, multi-select) project as arrays;
 * single-answer items project as the raw scalar. Unrecognised
 * value[x] keys collapse to `undefined` and are skipped.
 */
export function draftAnswersFromResource(resource: unknown): DraftAnswers {
  if (resource === null || typeof resource !== 'object') return {};
  const qr = resource as QuestionnaireResponse;
  if (qr.resourceType !== 'QuestionnaireResponse') return {};
  const out: DraftAnswers = {};
  for (const item of walkResponseItems(qr)) {
    const values = projectAnswers(item.answer).filter((v) => v !== undefined);
    if (values.length === 0) continue;
    out[item.linkId] = values.length === 1 ? values[0] : values;
  }
  return out;
}

/** Build a FHIR QuestionnaireResponse from the current intake store state. */
export function buildQuestionnaireResponse(args: {
  questionnaireRef: string;
  status: QuestionnaireResponse['status'];
  answers: DraftAnswers;
  /**
   * Optional. When provided, `toAnswer` uses the matching item type to
   * pick the right FHIR `value[x]` key (e.g. `valueDate` for a date item
   * instead of `valueString`). Without it, type inference falls back to
   * the JS value alone, which produces shapes the engine flags as `type`
   * mismatches. New callers should always pass this; the optional
   * parameter is for legacy call sites pending follow-up.
   */
  questionnaire?: Questionnaire | undefined;
}): QuestionnaireResponse {
  const itemTypes = args.questionnaire
    ? collectItemTypes(args.questionnaire)
    : new Map<string, string>();
  const allItems = Object.entries(args.answers)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([linkId, value]) => ({ linkId, answer: toAnswer(value, itemTypes.get(linkId)) }));
  const base: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    questionnaire: args.questionnaireRef,
    status: args.status,
    authored: new Date().toISOString(),
    item: allItems,
  };
  // Strip answers for items that are not currently active under the
  // questionnaire's `enableWhen` graph. Without this, a patient who
  // answers field A (revealing B), fills B, then changes A back so B
  // re-hides would submit B's stale value — the engine considers B
  // inactive, the patient can't see/edit it, but the data leaks through.
  // We iterate to a fixpoint because removing one inactive answer can
  // disable a transitively-dependent item too (B's value might have
  // been keeping C active). Each iteration only ever removes items,
  // so termination is bounded by the number of items.
  if (args.questionnaire === undefined) return base;
  let currentItems = allItems;
  let prevSize = -1;
  while (currentItems.length !== prevSize) {
    prevSize = currentItems.length;
    const activeLinkIds = resolveActiveItems(args.questionnaire, {
      ...base,
      item: currentItems,
    });
    currentItems = currentItems.filter((i) => activeLinkIds.has(i.linkId));
  }
  // Calculated items (a screener's total) are recomputed from the pruned
  // answers, so the draft, the review screen and the submit all carry the
  // score the answers imply. The API recomputes on submit regardless.
  return applyCalculations(args.questionnaire, { ...base, item: currentItems });
}

/** Flatten the questionnaire into a `linkId → item.type` lookup. */
function collectItemTypes(questionnaire: Questionnaire): Map<string, string> {
  const out = new Map<string, string>();
  const walk = (items: readonly QuestionnaireItem[] | undefined): void => {
    if (items === undefined) return;
    for (const it of items) {
      if (typeof it.type === 'string') out.set(it.linkId, it.type);
      if (it.type === 'group') walk(it.item);
    }
  };
  walk(questionnaire.item);
  return out;
}

function toAnswer(value: unknown, itemType: string | undefined): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.map((v) => toAnswer(v, itemType)[0] ?? { valueString: String(v) });
  }
  // Type-driven branches — these only apply when the host passed the
  // questionnaire. Without it, we fall through to JS-typeof inference.
  if (itemType !== undefined && value !== null) {
    switch (itemType) {
      case 'date':
        if (typeof value === 'string') return [{ valueDate: value }];
        break;
      case 'dateTime':
        if (typeof value === 'string') return [{ valueDateTime: value }];
        break;
      case 'time':
        if (typeof value === 'string') return [{ valueTime: value }];
        break;
      case 'integer':
        if (typeof value === 'number') return [{ valueInteger: Math.trunc(value) }];
        if (typeof value === 'string' && value !== '') {
          const n = parseInt(value, 10);
          if (Number.isFinite(n)) return [{ valueInteger: n }];
        }
        break;
      case 'decimal':
        if (typeof value === 'number') return [{ valueDecimal: value }];
        if (typeof value === 'string' && value !== '') {
          const n = parseFloat(value);
          if (Number.isFinite(n)) return [{ valueDecimal: n }];
        }
        break;
      case 'boolean':
        if (typeof value === 'boolean') return [{ valueBoolean: value }];
        break;
      case 'string':
      case 'text':
        if (typeof value === 'string') return [{ valueString: value }];
        break;
      // 'choice' / 'open-choice' carry Coding objects — handled below.
      // 'attachment' carriers — handled below.
      // 'reference' carries valueReference — handled below.
    }
  }
  // Fallthrough JS-typeof inference.
  if (typeof value === 'string') return [{ valueString: value }];
  if (typeof value === 'number') return [{ valueDecimal: value }];
  if (typeof value === 'boolean') return [{ valueBoolean: value }];
  if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (
      'valueCoding' in obj ||
      'valueString' in obj ||
      'valueInteger' in obj ||
      'valueDecimal' in obj ||
      'valueDate' in obj ||
      'valueDateTime' in obj ||
      'valueTime' in obj ||
      'valueBoolean' in obj ||
      'valueReference' in obj ||
      'valueAttachment' in obj
    ) {
      return [obj];
    }
    // Attachment carrier shape from the FileUpload / Signature renderers.
    // The carrier holds upload state (`_file`, `_status`, `_blob`) alongside
    // the FHIR Attachment scalars (`url`, `contentType`, `size`, `title`).
    // Project to a clean `valueAttachment` so the engine's type check
    // sees a real FHIR shape and not the implementation-detail carrier.
    if ('_file' in obj || '_blob' in obj || '_status' in obj || 'url' in obj) {
      const attachment: Record<string, unknown> = {};
      if (typeof obj['url'] === 'string') attachment['url'] = obj['url'];
      if (typeof obj['contentType'] === 'string') attachment['contentType'] = obj['contentType'];
      if (typeof obj['size'] === 'number') attachment['size'] = obj['size'];
      if (typeof obj['title'] === 'string') attachment['title'] = obj['title'];
      return [{ valueAttachment: attachment }];
    }
    return [{ valueCoding: value }];
  }
  return [{ valueString: String(value) }];
}
